import React from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../../components/loading/Loading';
import useSetupWizard from './hooks/useSetupWizard';
import { SETUP_STEPS } from './constants/setupSteps';
import WizardProgress from './components/WizardProgress';
import AreaStep from './steps/AreaStep';
import IdentityStep from './steps/IdentityStep';
import DemoStep from './steps/DemoStep';
import DoneScreen from './steps/DoneScreen';
import PreparingScreen from './steps/PreparingScreen';
import TutorialsBanner from '../../components/tutorial-help/TutorialsBanner';
import LogoFull from '../../assets/images/logo-full.png';
import styles from './SetupWizard.module.css';

const STEP_COMPONENTS = {
  area: AreaStep,
  identity: IdentityStep,
  demo: DemoStep,
};

/**
 * Configuração inicial v2: três passos curtos (área, você e a empresa, teste no
 * WhatsApp) que terminam com a pessoa recebendo, no próprio WhatsApp, a confirmação
 * de um agendamento de teste. Horários e atendimentos saem dos padrões da área. Na
 * conta em teste, os exemplos da área são criados em segundo plano assim que o passo 2
 * é salvo; concluir vai para "Pronto" (ou mostra "Estamos preparando sua conta" se os
 * exemplos ainda não terminaram), que começa o guia. Na primeira vez a tela ocupa tudo (App.js
 * esconde o menu e o cabeçalho) e não dá para pular: só sair da conta.
 */
function SetupWizard() {
  const wizard = useSetupWizard();
  const navigate = useNavigate();
  const { data, loadError, reload, stepIndex, stepKey, finished, preparing, seedDone, complete, skip } = wizard;

  if (loadError) {
    return (
      <div className="page-content">
        <div className={styles.wizard}>
          <div className={styles.loadError}>
            <p>Não foi possível abrir a configuração agora.</p>
            <button type="button" className={styles.primaryButton} onClick={reload}>Tentar de novo</button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="page-content"><Loading /></div>;
  }

  const StepComponent = STEP_COMPONENTS[stepKey];
  const firstName = (data.identity.person_name || '').split(' ')[0];

  return (
    <div className="page-content">
      <div className={styles.wizard}>
        {/* Sem menu nem cabeçalho na configuração (App.js, modo foco): a marca fica aqui */}
        <img className={styles.brand} src={LogoFull} alt="Gabriela" />
        {finished && <DoneScreen data={data} />}
        {!finished && preparing && <PreparingScreen data={data} ready={seedDone} onDone={complete} />}
        {!finished && !preparing && (
          <>
            <header className={styles.header}>
              <div>
                <span className={styles.badge}>Configuração inicial</span>
                <h1 className={styles.title}>
                  {stepIndex === 0 && firstName ? `Boas-vindas, ${firstName}` : 'Vamos deixar tudo pronto'}
                </h1>
                <p className={styles.subtitle}>
                  Poucos minutos e a sua agenda já começa a trabalhar por você.
                </p>
              </div>
              {data.status === 'INITIAL' || data.status === 'IN_PROGRESS' ? (
                <button type="button" className={styles.skipButton} onClick={() => navigate('/logout')}>
                  Sair da conta
                </button>
              ) : (
                <button type="button" className={styles.skipButton} onClick={skip}>
                  Fazer depois
                </button>
              )}
            </header>

            <WizardProgress steps={SETUP_STEPS} current={stepIndex} />

            <section className={styles.card} key={stepKey}>
              <StepComponent data={data} wizard={wizard} />
            </section>

            <div className={styles.help}>
              <TutorialsBanner inline compact />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default SetupWizard;
